<template>
    <div class="container-fluid px-5 mt-4">

        <!-- Nadpis stránky + tlačítka -->
        <div class="d-flex align-items-center justify-content-between mb-4 certs-page-header">
            <h1 class="credits-page__title">{{ $t('file.files') }}</h1>
            <div class="d-flex gap-2">
                <button class="btn certs-add-folder-btn" @click="showAddFolderModal = true">
                    {{ $t('cert.addFolder') }}
                    <img :src="iconFolder" alt="" class="certs-add-folder-btn__icon">
                </button>
                <button class="btn btn-primary" style="height:40px;padding:0 16px;font-size:14px;font-weight:500;" @click="showUploadModal = true">
                    {{ $t('file.uploadNewFile') }}
                </button>
            </div>
        </div>

        <!-- Sekce Moje složky -->
        <div class="dashboard-section-header mb-3">
            <div class="d-flex align-items-center gap-1 flex-wrap folder-breadcrumb">
                <img :src="iconFolder" alt="" class="dashboard-section-header__icon me-1" style="width:14px;height:14px;filter:brightness(0) invert(88%) sepia(6%) saturate(362%) hue-rotate(202deg) brightness(93%) contrast(87%)">
                <span v-if="!folderPath.length" class="dashboard-section-header__title">{{ $t('cert.myFolders') }}</span>
                <template v-else>
                    <button class="folder-breadcrumb__link" @click="navigateTo(-1)">{{ $t('cert.myFolders') }}</button>
                    <template v-for="(f, i) in folderPath" :key="f.uuid">
                        <i class="bi bi-chevron-right folder-breadcrumb__sep"></i>
                        <button v-if="i < folderPath.length - 1" class="folder-breadcrumb__link" @click="navigateTo(i)">{{ f.name }}</button>
                        <span v-else class="dashboard-section-header__title">{{ f.name }}</span>
                    </template>
                </template>
                <span class="count-badge ms-1">{{ folders.length }}</span>
            </div>
            <button v-if="folders.length > 3 && !showAllFolders" type="button" class="dashboard-show-all" @click="showAllFolders = true">
                {{ $t('cert.viewAll') }}
                <i class="bi bi-chevron-right dashboard-show-all__chevron"></i>
            </button>
        </div>

        <!-- Složky jako karty -->
        <div v-if="!foldersLoading && folders.length > 0" class="folder-cards-row mb-5">
            <div
                v-for="folder in visibleFolders"
                :key="folder.uuid"
                class="folder-card dashboard-action-box--clickable"
                :class="{ 'folder-card--active': selectedFolder && selectedFolder.uuid === folder.uuid }"
                @click="selectFolder(folder)"
            >
                <div class="folder-card__icon">
                    <img :src="iconFolder" alt="" style="filter:brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)">
                </div>
                <p class="folder-card__name">{{ folder.name }}</p>
                <p class="folder-card__count">&nbsp;</p>
                <button
                    class="folder-card__delete-btn"
                    @click.stop="deleteFolder(folder)"
                    :title="$t('file.deleteFolder')"
                    :disabled="deletingFolder === folder.uuid"
                >
                    <span v-if="deletingFolder === folder.uuid" class="spinner-border spinner-border-sm" role="status"></span>
                    <i v-else class="bi bi-trash3"></i>
                </button>
            </div>
        </div>

        <!-- Empty state složky -->
        <div v-if="!foldersLoading && folders.length === 0" class="d-flex justify-content-center mt-4 mb-5">
            <EmptyStateComponent
                :icon="iconFolder"
                :icon-style="{ width: '17px', height: '17px' }"
                :title="$t('cert.emptyFoldersTitle')"
                :text="$t('cert.emptyFoldersText')"
            >
                <button class="btn certs-add-folder-btn" @click="showAddFolderModal = true">
                    {{ $t('cert.addFolder') }}
                    <img :src="iconFolder" alt="" class="certs-add-folder-btn__icon">
                </button>
            </EmptyStateComponent>
        </div>

        <!-- Modal Přidat složku -->
        <div v-if="showAddFolderModal" class="create-cert-overlay" @click.self="showAddFolderModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('cert.addFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showAddFolderModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <label class="create-cert-label mb-1">{{ $t('file.name') }}</label>
                    <input
                        type="text"
                        class="form-control mb-4"
                        v-model="newFolderName"
                        :placeholder="$t('file.newFolder')"
                    >
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-primary" @click="createFolder" :disabled="!newFolderName || creatingFolder">
                            <span v-if="creatingFolder" class="spinner-border spinner-border-sm me-1" role="status"></span>
                            {{ $t('file.create') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Nahrát soubor -->
        <div v-if="showUploadModal" class="create-cert-overlay" @click.self="!uploading && closeUploadModal()">
            <div class="create-cert-modal" style="max-width:480px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('file.uploadNewFile') }}</span>
                    <button class="create-cert-modal__close" @click="!uploading && closeUploadModal()" :disabled="uploading">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <div
                        class="create-cert-dropzone"
                        :class="{ 'create-cert-dropzone--active': isDragging, 'create-cert-dropzone--filled': uploadFile }"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="onDrop"
                        @click="$refs.fileInput.click()"
                    >
                        <img :src="iconFile" alt="" class="create-cert-dropzone__icon">
                        <span v-if="!uploadFile" class="create-cert-dropzone__text">{{ $t('cert.uploadFile') }}</span>
                        <span v-else class="create-cert-dropzone__filename">{{ uploadFile.name }}</span>
                        <input type="file" ref="fileInput" style="display:none" @change="onFileChanged">
                    </div>
                    <p class="create-cert-hint mt-2">{{ $t('cert.maxFileSizeText') }}</p>

                    <div v-if="selectedFolder" class="mt-3">
                        <span class="create-cert-label">{{ $t('file.folder') }}: </span>
                        <span class="fw-semibold">{{ selectedFolder.name }}</span>
                    </div>

                    <div class="d-flex justify-content-end gap-3 mt-4">
                        <button class="btn create-cert-btn-cancel" type="button" @click="closeUploadModal">
                            {{ $t('general.cancel') }}
                        </button>
                        <button class="btn btn-primary create-cert-btn-submit" type="button" @click="uploadFileStandalone" :disabled="uploading || !uploadFile">
                            <span v-if="uploading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ uploading ? $t('cert.processing') : $t('file.uploadNewFile') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Přesunout soubor -->
        <div v-if="showMoveModal" class="create-cert-overlay" @click.self="showMoveModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('file.moveToFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showMoveModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <p class="create-cert-label mb-3">{{ moveTargetFile?.name }}</p>
                    <div v-if="moveFoldersLoading" class="text-center py-3">
                        <span class="spinner-border spinner-border-sm" role="status"></span>
                    </div>
                    <div v-else>
                        <div v-if="!moveFolders.length" class="text-muted text-center py-2" style="font-size:.875rem;">
                            {{ $t('file.noFolders') }}
                        </div>
                        <div
                            v-for="folder in moveFolders"
                            :key="folder.uuid"
                            class="move-folder-item"
                            :class="{ 'move-folder-item--moving': movingToFolder === folder.uuid }"
                            :style="{ paddingLeft: (12 + folder.depth * 20) + 'px' }"
                            @click="movingToFolder || moveFileToFolder(folder.uuid)"
                        >
                            <span v-if="movingToFolder === folder.uuid" class="spinner-border spinner-border-sm text-primary flex-shrink-0" role="status"></span>
                            <img v-else :src="iconFolder" alt="" style="width:14px;height:14px;flex-shrink:0;filter:brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)">
                            {{ folder.name }}
                        </div>
                        <div
                            v-if="moveTargetFile && moveTargetFile.parent_uuid"
                            class="move-folder-item move-folder-item--root"
                            @click="movingToFolder || moveFileToFolder(null)"
                        >
                            <span v-if="movingToFolder === 'root'" class="spinner-border spinner-border-sm text-primary me-2 flex-shrink-0" role="status"></span>
                            <i v-else class="bi bi-house me-2"></i>{{ $t('file.moveToRoot') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sekce Nahrané soubory / obsah složky -->
        <div class="dashboard-section-header mb-3 mt-2">
            <div class="d-flex align-items-center gap-2">
                <img :src="iconFile" alt="" class="dashboard-section-header__icon" style="width:14px;height:14px;filter:brightness(0) invert(88%) sepia(6%) saturate(362%) hue-rotate(202deg) brightness(93%) contrast(87%)">
                <span class="dashboard-section-header__title">{{ filesSectionTitle }}</span>
                <span class="count-badge ms-2">{{ totalCount }}</span>
            </div>
            <button v-if="selectedFolder" class="btn cert-list__view-btn" @click="navigateTo(-1)">
                <i class="bi bi-chevron-left me-1"></i>{{ $t('file.allFiles') }}
            </button>
        </div>

        <!-- Toolbar řádek 1: vyhledávání + řazení -->
        <div class="certs-toolbar mb-2">
            <div class="certs-search">
                <img :src="iconMagnifying" alt="" class="certs-search__icon">
                <input
                    type="text"
                    class="certs-search__input"
                    v-model="searchQuery"
                    :placeholder="$t('file.searchPlaceholder')"
                >
            </div>
            <div class="certs-sort">
                <button
                    class="certs-sort__btn"
                    :class="{ 'certs-sort__btn--active': sortColumn === 'name' }"
                    @click="toggleSort('name')"
                >
                    {{ $t('file.sortByName') }}
                    <i class="bi" :class="sortColumn === 'name' ? (sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-down-up'"></i>
                </button>
                <button
                    class="certs-sort__btn"
                    :class="{ 'certs-sort__btn--active': sortColumn === 'created_at' }"
                    @click="toggleSort('created_at')"
                >
                    {{ $t('file.sortByDate') }}
                    <i class="bi" :class="sortColumn === 'created_at' ? (sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-down-up'"></i>
                </button>
            </div>
        </div>

        <!-- Toolbar řádek 2: filtr dle typu souboru (zarovnán vpravo) -->
        <div class="certs-toolbar mb-3">
            <div></div>
            <div class="certs-filters">
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === '' && !!selectedFolder }" @click="fileTypeFilter = ''">{{ $t('file.filterTypeAll') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'document' }" @click="fileTypeFilter = 'document'">{{ $t('file.filterTypeDocument') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'photo' }" @click="fileTypeFilter = 'photo'">{{ $t('file.filterTypePhoto') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'video' }" @click="fileTypeFilter = 'video'">{{ $t('file.filterTypeVideo') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'audio' }" @click="fileTypeFilter = 'audio'">{{ $t('file.filterTypeAudio') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'archive' }" @click="fileTypeFilter = 'archive'">{{ $t('file.filterTypeArchive') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'other' }" @click="fileTypeFilter = 'other'">{{ $t('file.filterTypeOther') }}</button>
            </div>
        </div>

        <Skeletor v-if="loading" />

        <!-- Empty state — žádné soubory vůbec -->
        <div v-if="!loading && baseCount === 0" class="d-flex justify-content-center mt-5 pt-4">
            <EmptyStateComponent
                :icon="iconFile"
                :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
                :title="$t('file.emptyFilesTitle')"
                :text="$t('file.emptyFilesText')"
            >
                <button class="btn btn-primary app-nav__create-btn" @click="showUploadModal = true">
                    {{ $t('file.uploadNewFile') }} <img :src="iconPlus" alt="">
                </button>
            </EmptyStateComponent>
        </div>

        <!-- Empty state — filtr/hledání bez výsledků -->
        <div v-if="!loading && baseCount !== 0 && !filteredFiles.length" class="d-flex justify-content-center mt-5 pt-2">
            <EmptyStateComponent
                :icon="iconFile"
                :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
                :title="$t('file.noFilterResults')"
                :text="$t('file.noFilterResultsText')"
            />
        </div>

        <div v-if="!loading && filteredFiles.length" class="cert-list cert-list--certs-mobile">
            <div
                class="cert-list__row"
                v-for="file in filteredFiles"
                :key="file.uuid"
            >
                <div class="cert-list__icon-wrap">
                    <i :class="['bi', fileTypeIcon(file).cls, 'cert-list__file-icon']" :style="{ color: fileTypeIcon(file).color }"></i>
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('file.fileName') }}</span>
                    <span class="cert-list__value">{{ file.name }}</span>
                </div>

                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('file.fileCreatedDate') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(file.created_at) }}, {{ formatTime(file.created_at) }}</span>
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('file.folder') }}</span>
                    <span class="cert-list__value">{{ file.folder_name ?? '—' }}</span>
                </div>

                <div class="cert-list__action">
                    <span
                        class="cert-list__sharing-icon d-none d-md-inline-block"
                        :class="{ 'cert-list__sharing-icon--inactive': !['link', 'logged'].includes(file.sharing_settings?.mode) }"
                        :style="{ maskImage: `url(${iconSharingChain})`, webkitMaskImage: `url(${iconSharingChain})` }"
                    ></span>
                    <button
                        class="btn cert-list__view-btn cert-list__view-btn--icon"
                        :title="$t('file.moveFile')"
                        @click="openMoveModal(file)"
                    >
                        <i class="bi bi-folder-symlink"></i>
                    </button>
                    <button
                        v-if="!file.cert_id"
                        class="btn cert-list__view-btn cert-list__view-btn--icon"
                        :title="$t('cert.certCreate')"
                        @click="generateCertFromFile(file)"
                        :disabled="generatingCertForFile === file.uuid"
                    >
                        <span v-if="generatingCertForFile === file.uuid" class="spinner-border spinner-border-sm" role="status"></span>
                        <i v-else class="bi bi-plus-circle"></i>
                    </button>
                    <router-link
                        v-if="file.cert_id && file.cert"
                        class="btn cert-list__view-btn cert-list__view-btn--icon"
                        :title="$t('cert.viewCertificate')"
                        :to="{ name: 'CertDetail', params: { uuid: file.cert.uuid } }"
                    >
                        <i class="bi bi-file-earmark-check"></i>
                    </router-link>
                  <router-link
                      class="btn cert-list__view-btn cert-list__view-btn--icon"
                      :title="$t('file.viewFile')"
                      :to="{ name: 'FileDetail', params: { uuid: file.uuid } }"
                  >
                    <i class="bi bi-eye"></i>
                  </router-link>
                </div>
            </div>
        </div>

        <!-- Načíst více -->
        <div v-if="!loading && loadMoreAvailable" class="d-flex justify-content-center mt-4 mb-5">
            <button class="btn cert-list__view-btn" @click="loadMore">
                {{ $t('file.loadMore') }}
            </button>
        </div>

    </div>
</template>

<script>
import http from '@/http';
import { formatDateShort } from '@/utils/date.js';
import { checkFileSize } from '@/utils/upload.js';
import { loadingState } from '@/loading.js';
import iconFile from '/public/img/new-design/icons/file.svg';
import iconFolderFill from '/public/img/new-design/icons/folder-fill.svg';
import iconFolderBlue from '/public/img/new-design/icons/folder-fill-blue.svg';
import iconMagnifying from '/public/img/new-design/icons/magnifying.svg';
import iconPlus from '/public/img/new-design/icons/plus.svg';
import iconFolder from '/public/img/new-design/icons/folder.svg';
import iconSharingChain from '/public/img/new-design/icons/sharing.svg';

export default {
    mounted() {
        this.fetchItems();
        this.emitter.on('fileUploaded', (raw) => {
            const file = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (file && file.uuid) {
                this.filesList.unshift(file);
                this.totalCount++;
            }
        });
    },

    watch: {
        fileTypeFilter() { this.page = 1; this.filesList = []; this.loadMoreAvailable = false; this.fetchItems(); },
        sortColumn()     { this.page = 1; this.filesList = []; this.loadMoreAvailable = false; this.fetchItems(); },
        sortDirection()  { this.page = 1; this.filesList = []; this.loadMoreAvailable = false; this.fetchItems(); },
        searchQuery() {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.page = 1;
                this.filesList = [];
                this.loadMoreAvailable = false;
                this.fetchItems();
            }, 400);
        },
    },

    data() {
        return {
            folders: [],
            folderPath: [],
            filesList: [],
            loading: true,
            foldersLoading: true,
            selectedFolder: null,
            showAddFolderModal: false,
            showAllFolders: false,
            showUploadModal: false,
            newFolderName: '',
            uploadFile: null,
            isDragging: false,
            uploading: false,
            searchQuery: '',
            fileTypeFilter: '',
            sortColumn: 'name',
            sortDirection: 'asc',
            searchDebounce: null,
            page: 1,
            perPage: 20,
            totalCount: 0,
            baseCount: null,
            loadMoreAvailable: false,
            generatingCertForFile: null,
            creatingFolder: false,
            deletingFolder: null,
            showMoveModal: false,
            moveTargetFile: null,
            moveFolders: [],
            moveFoldersLoading: false,
            movingToFolder: null,
            iconFile,
            iconFolderFill,
            iconFolderBlue,
            iconMagnifying,
            iconPlus,
            iconFolder,
            iconSharingChain,
        };
    },

    computed: {
        visibleFolders() {
            return this.showAllFolders ? this.folders : this.folders.slice(0, 4);
        },

        filesSectionTitle() {
            return this.selectedFolder
                ? this.$t('file.uploadedFilesIn') + ' ' + this.selectedFolder.name
                : this.$t('file.uploadedFiles');
        },

        filteredFiles() {
            return this.filesList;
        },
    },

    methods: {
        fileTypeIcon(file) {
            const mime = file?.mime_type ?? '';
            const ext = (file?.extension || (file?.filename ?? '').split('.').pop() || '').toLowerCase();
            if (mime === 'application/pdf' || ext === 'pdf')
                return { cls: 'bi-file-earmark-pdf', color: '#e74c3c' };
            if (mime.startsWith('image/') || ['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','svg'].includes(ext))
                return { cls: 'bi-file-earmark-image', color: '#3498db' };
            if (mime.includes('wordprocessingml') || mime === 'application/msword' || ['doc','docx','odt'].includes(ext))
                return { cls: 'bi-file-earmark-word', color: '#2b579a' };
            if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel' || ['xls','xlsx','ods','csv'].includes(ext))
                return { cls: 'bi-file-earmark-excel', color: '#217346' };
            if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint' || ['ppt','pptx','odp'].includes(ext))
                return { cls: 'bi-file-earmark-ppt', color: '#d24726' };
            if (mime.startsWith('audio/') || ['mp3','wav','ogg','flac','aac','m4a'].includes(ext))
                return { cls: 'bi-file-earmark-music', color: '#1abc9c' };
            if (mime.startsWith('video/') || ['mp4','avi','mov','mkv','webm','m4v'].includes(ext))
                return { cls: 'bi-file-earmark-play', color: '#e67e22' };
            if (['zip','rar','7z','tar','gz','bz2'].includes(ext) || mime.includes('zip'))
                return { cls: 'bi-file-earmark-zip', color: '#8e44ad' };
            if (mime.startsWith('text/') || ['txt','md','rtf'].includes(ext))
                return { cls: 'bi-file-earmark-text', color: '#7f8c8d' };
            if (['php','js','ts','py','html','css','json','xml','sql'].includes(ext))
                return { cls: 'bi-file-earmark-code', color: '#27ae60' };
            return { cls: 'bi-file-earmark', color: '#6B7280' };
        },

        toggleSort(column) {
            if (this.sortColumn === column) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortColumn = column;
                this.sortDirection = column === 'name' ? 'asc' : 'desc';
            }
        },

        fetchItems(append = false) {
            if (!append) {
                this.loading = true;
                this.foldersLoading = true;
            }

            const uuid = this.selectedFolder?.uuid;
            const path = uuid ? `/files/folders/${uuid}` : '/files/folders';
            const params = {
                perPage:        this.perPage,
                page:           this.page,
                orderColumn:    this.sortColumn,
                orderDirection: this.sortDirection,
            };
            if (!uuid) params.allFolders = 1;
            if (this.searchQuery.trim())     params.name = this.searchQuery.trim();
            if (this.fileTypeFilter)         params.fileType = this.fileTypeFilter;

            http.get(path, { params }).then((response) => {
                const items = response.data.files || [];
                const folders = items.filter(f => f.type === 'FOLDER');
                const files = items.filter(f => f.type !== 'FOLDER');

                this.folders = folders;

                if (append) {
                    this.filesList = this.filesList.concat(files);
                } else {
                    this.filesList = files;
                }

                this.loadMoreAvailable = files.length === this.perPage;
                this.totalCount = response.data.files_total ?? this.filesList.length;
                if (!params.name && !params.fileType) {
                    this.baseCount = response.data.files_total ?? this.filesList.length;
                }
                this.loading = false;
                this.foldersLoading = false;
                loadingState.active = false;
            });
        },

        loadMore() {
            this.page++;
            this.fetchItems(true);
        },

        selectFolder(folder) {
            if (this.selectedFolder?.uuid === folder.uuid) return;
            this.folderPath.push(folder);
            this.selectedFolder = folder;
            this.page = 1;
            this.filesList = [];
            this.showAllFolders = false;
            this.fetchItems();
        },

        navigateTo(index) {
            if (index === -1) {
                this.selectedFolder = null;
                this.folderPath = [];
            } else {
                this.selectedFolder = this.folderPath[index];
                this.folderPath = this.folderPath.slice(0, index + 1);
            }
            this.page = 1;
            this.filesList = [];
            this.showAllFolders = false;
            this.fetchItems();
        },

        createFolder() {
            if (!this.newFolderName || this.creatingFolder) return;
            this.creatingFolder = true;
            const payload = { name: this.newFolderName };
            if (this.selectedFolder) payload.uuid = this.selectedFolder.uuid;
            http.post('/files/folders/create', payload).then((response) => {
                if (response.data.file) {
                    this.folders.unshift(response.data.file);
                }
                this.showAddFolderModal = false;
                this.newFolderName = '';
            }).finally(() => {
                this.creatingFolder = false;
            });
        },

        onDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            if (file) this.setUploadFile(file);
        },

        onFileChanged(event) {
            const file = event.target.files[0];
            if (file) this.setUploadFile(file);
        },

        setUploadFile(file) {
            if (!checkFileSize(file)) return;
            this.uploadFile = file;
        },

        closeUploadModal() {
            this.showUploadModal = false;
            this.uploadFile = null;
            this.isDragging = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },

        uploadFileStandalone() {
            if (!this.uploadFile) return;
            this.uploading = true;

            const body = new FormData();
            body.append('file', this.uploadFile);
            if (this.selectedFolder) {
                body.append('folderUuid', this.selectedFolder.uuid);
            }

            http.post('/files/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } })
                .then((response) => {
                    const file = response.data?.file || response.data;
                    if (file && file.uuid) {
                        this.filesList.unshift(file);
                        this.totalCount++;
                    }
                    this.uploading = false;
                    this.closeUploadModal();
                })
                .catch(() => {
                    this.uploading = false;
                });
        },

        deleteFolder(folder) {
            const count = folder.children_count || 0;
            const msg = count > 0
                ? this.$t('file.confirmDeleteFolderWithFiles', { count })
                : this.$t('file.confirmDeleteFolder');

            this.$swal.fire({
                title: msg,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6c757d',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    this.deletingFolder = folder.uuid;
                    http.post(`/files/folders/${folder.uuid}/delete`).then(() => {
                        this.folders = this.folders.filter(f => f.uuid !== folder.uuid);
                    }).finally(() => {
                        this.deletingFolder = null;
                    });
                }
            });
        },

        openMoveModal(file) {
            this.moveTargetFile = file;
            this.showMoveModal = true;
            this.moveFoldersLoading = true;
            http.get('/files/folders/all').then((response) => {
                this.moveFolders = (response.data.folders || []).filter(f => f.uuid !== file.uuid);
            }).finally(() => {
                this.moveFoldersLoading = false;
            });
        },

        moveFileToFolder(folderUuid) {
            this.movingToFolder = folderUuid ?? 'root';
            http.post(`/files/${this.moveTargetFile.uuid}/move`, { folder_uuid: folderUuid }).then((response) => {
                const updatedFile = response.data?.file;
                if (updatedFile) {
                    const idx = this.filesList.findIndex(f => f.uuid === this.moveTargetFile.uuid);
                    if (idx !== -1) this.filesList[idx] = updatedFile;
                }
                this.showMoveModal = false;
                this.moveTargetFile = null;
            }).finally(() => {
                this.movingToFolder = null;
            });
        },

        generateCertFromFile(file) {
            this.generatingCertForFile = file.uuid;
            // Server potřebuje jen id souboru
            http.post('/certs/generate', { file_model: { id: file.id } })
                .then((response) => {
                    const updatedFile = response.data.file;
                    if (updatedFile) {
                        const idx = this.filesList.findIndex(f => f.uuid === file.uuid);
                        if (idx !== -1) this.filesList[idx] = updatedFile;
                    }
                    if (window.updateCreditCount) window.updateCreditCount();
                    this.generatingCertForFile = null;
                })
                .catch(() => {
                    this.generatingCertForFile = null;
                });
        },

        // Sdílená funkce – zkratka dne podle jazyka aplikace
        formatDateShort,

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style scoped>
.files-upload-btn {
    display: flex;
    align-items: center;
    gap: 8px;
}

.folder-card--active {
    border-color: #175cff !important;
    background: #f0f5ff;
}

.folder-card {
    position: relative;
}

.folder-card__delete-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    background: none;
    border: none;
    padding: 4px 6px;
    color: #d33;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    border-radius: 4px;
    display: none;
}
.folder-card:hover .folder-card__delete-btn {
    display: block;
}
.folder-card__delete-btn:hover {
    background: #fff0f0;
}


.move-folder-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    color: #1a1a2e;
    border: 1px solid transparent;
    margin-bottom: 4px;
    transition: background .15s;
}
.move-folder-item:hover {
    background: #f0f5ff;
    border-color: #c7d9ff;
}
.move-folder-item--root {
    margin-top: 8px;
    color: #6b7280;
    border-top: 1px solid #e5e7eb;
    border-radius: 0;
    padding-top: 12px;
}
</style>